import React, { useState, useEffect, useMemo, useCallback } from "react";
import DataGridList from "./grid/DataGridList";
import api from "../services/axiosInstance";
import ServerUpdateDialog from "./ServerUpdateDialog";
import ConfirmDialog from "./ConfirmDialog";
import ServerEditDialog from "./ServerEditDialog";
import {
  renderGroupLabel, calculateGroupLabel,
  renderSqlVersion, calculateSqlVersion,
  makeVersionCell, renderTypeBadge, renderStatusBadge, renderSsisBadge,
  applyRowStatusClass,
} from "../utils/columnRenderers";
import { useMonitoring } from "../context/MonitoringContext";

const compareVersions = (v1, v2) => {
  const p1 = String(v1 || "0").replace(/^v/i, "").split(".").map(Number);
  const p2 = String(v2 || "0").replace(/^v/i, "").split(".").map(Number);
  const len = Math.max(p1.length, p2.length);
  for (let i = 0; i < len; i++) {
    const n1 = p1[i] || 0;
    const n2 = p2[i] || 0;
    if (n1 > n2) return 1;
    if (n1 < n2) return -1;
  }
  return 0;
};

export default function ServerFleetList({
  servers,
  onTriggerAction,
  onViewLogs,
  onRefresh,
  onUpdateAll,
  loading,
  refreshRate,
  setRefreshRate
}) {
  const { notify } = useMonitoring();
  const [serverFilter, setServerFilter] = useState("all");
  const [updateDialogServer, setUpdateDialogServer] = useState(null);
  const [deleteDialogServer, setDeleteDialogServer] = useState(null);
  const [editDialogServer, setEditDialogServer] = useState(null);
  const [serverTypes, setServerTypes] = useState([]);

  useEffect(() => {
    api.get("/servertypes")
      .then(res => {
        setServerTypes(res.data || []);
      })
      .catch(err => console.error("Failed to load server types", err));
  }, []);

  const filterOptions = useMemo(() => [
    { text: "All", value: "all" },
    ...serverTypes.map(st => ({ text: st.name, value: st.name.toLowerCase() }))
  ], [serverTypes]);

  const getActionButtonText = useCallback((s) => {
    const hoLt = compareVersions(s.hoVersion, s.targetHoVersion) < 0;
    const dsLt = compareVersions(s.dsVersion, s.targetDsVersion) < 0;
    const ssLt = compareVersions(s.ssVersion, s.targetSsVersion) < 0;

    if (hoLt || dsLt || ssLt) {
      return "Update";
    }

    const hoGt = compareVersions(s.hoVersion, s.targetHoVersion) > 0;
    const dsGt = compareVersions(s.dsVersion, s.targetDsVersion) > 0;
    const ssGt = compareVersions(s.ssVersion, s.targetSsVersion) > 0;

    if (hoGt || dsGt || ssGt) {
      return "Rollback";
    }

    return "Update";
  }, []);

  const isUpdateRunning = useCallback((status) => {
    if (!status) return false;
    const s = status.toLowerCase();
    return s !== "online" && s !== "offline";
  }, []);
  const isAnyUpdateRunning = servers.some(s => isUpdateRunning(s.status));

  // Confirmation is handled by ConfirmDialog (see render) rather than window.confirm.
  const handleDeleteServer = useCallback(async (server) => {
    const label = server.displayName || server.groupName || server.serverId;
    const dbServerId = server.dbServerId;
    if (!dbServerId) {
      notify("Cannot delete: this server has no database record.");
      return;
    }
    try {
      await api.delete(`/servers/${dbServerId}`);
      notify(`Server "${label}" deleted successfully.`);
      if (onRefresh) {
        await onRefresh();
      }
    } catch (err) {
      notify(`Failed to delete server: ${err.message}`);
    }
  }, [notify, onRefresh]);

  // Saves the operator-owned fields from ServerEditDialog. Editing lives in the Actions column
  // rather than inline in the grid, so every row operation is reached the same way.
  const handleSaveServer = useCallback(async (server, payload) => {
    const dbServerId = server?.dbServerId;
    if (!dbServerId) {
      notify("Cannot update: this server has no database record yet.");
      return;
    }
    try {
      await api.put(`/servers/${dbServerId}`, payload);
      notify("Server updated successfully.");
      if (onRefresh) {
        await onRefresh();
      }
    } catch (err) {
      notify(`Failed to update server: ${err.message}`);
    }
  }, [notify, onRefresh]);

  const columns = useMemo(() => [
    {
      dataField: "displayName",
      caption: "Group",
      sortOrder: "asc",
      allowEditing: false,
      calculateCellValue: calculateGroupLabel,
      calculateDisplayValue: calculateGroupLabel,
      cellRender: renderGroupLabel
    },
    {
      dataField: "serverId",
      caption: "Instance",
      allowEditing: false,
      cellRender: (data) => (
        <span style={{ fontFamily: "monospace", color: "#9aa2b1" }}>
          {data.value}
        </span>
      ),
      width: 140
    },
    {
      dataField: "sqlVersion",
      caption: "SQL Version",
      allowEditing: false,
      calculateCellValue: calculateSqlVersion,
      calculateDisplayValue: calculateSqlVersion,
      cellRender: renderSqlVersion
    },
    {
      dataField: "hoVersion",
      caption: "HO",
      allowEditing: false,
      cellRender: makeVersionCell("hoVersion", "targetHoVersion")
    },
    {
      dataField: "dsVersion",
      caption: "DS",
      allowEditing: false,
      cellRender: makeVersionCell("dsVersion", "targetDsVersion")
    },
    {
      dataField: "ssVersion",
      caption: "SS",
      allowEditing: false,
      cellRender: makeVersionCell("ssVersion", "targetSsVersion")
    },
    {
      dataField: "lastSoftwareUpdate",
      caption: "Last Updated",
      allowEditing: false,
      dataType: "datetime",
      format: "shortDateShortTime",
      cellRender: (data) => {
        if (!data.value) return <span style={{ color: "#9aa2b1" }}>-</span>;
        return <span style={{ color: "#d1d5db" }}>{new Date(data.value).toLocaleString()}</span>;
      }
    },
    {
      dataField: "serverType",
      caption: "Type",
      allowEditing: false,
      width: 90,
      cellRender: renderTypeBadge
    },
    {
      dataField: "ssisInstalled",
      caption: "SSIS",
      allowEditing: false,
      width: 100,
      cellRender: renderSsisBadge
    },
    {
      dataField: "status",
      name: "statusColumn",
      caption: "Status",
      allowEditing: false,
      cellRender: renderStatusBadge
    },
    {
      dataField: "status",
      name: "progressColumn",
      caption: "Progress",
      allowEditing: false,
      visible: isAnyUpdateRunning,
      cellRender: (data) => {
        const s = data.data;
        const running = isUpdateRunning(s.status);
        if (running) {
          return (
            <span style={{ color: "#EF9F27", fontWeight: "500", display: "flex", alignItems: "center", gap: "6px" }}>
              <span className="spinner" style={{
                width: "12px",
                height: "12px",
                border: "2px solid #EF9F27",
                borderTopColor: "transparent",
                borderRadius: "50%",
                display: "inline-block",
                animation: "spin 1s linear infinite"
              }} />
              {s.status}
            </span>
          );
        }
        return <span style={{ color: "#9aa2b1" }}>-</span>;
      }
    },
    {
      name: "actionsColumn",
      caption: "Actions",
      width: 200,
      cellRender: (data) => {
        const s = data.data;
        const actionText = getActionButtonText(s);
        const isRollback = actionText === "Rollback";

        return (
          <div style={{ display: "flex", gap: "6px", justifyContent: "center", alignItems: "center" }}>
            <button
              className="action-icon-btn"
              onClick={() => setEditDialogServer(s)}
              title="Edit group name and release channel"
              style={{
                color: "var(--brand-light)",
                borderColor: "rgba(148, 163, 184, 0.3)"
              }}
            >
              {/* Pencil / Edit Icon */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
            <button
              className="action-icon-btn"
              onClick={() => setUpdateDialogServer(s)}
              title={`${actionText} - choose version`}
              style={{
                color: isRollback ? "#ef9f27" : "#52c48d",
                borderColor: isRollback ? "rgba(239, 159, 39, 0.3)" : "rgba(82, 196, 141, 0.3)"
              }}
            >
              {isRollback ? (
                /* Rollback / Undo Icon */
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 7v6h6" />
                  <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
                </svg>
              ) : (
                /* Update / Cloud Download Icon */
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10" />
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                </svg>
              )}
            </button>
            <button
              className="action-icon-btn"
              onClick={() => onTriggerAction(s.serverId, "OptimizeDb")}
              title="Database Maintenance"
            >
              {/* Wrench / Maintenance Icon */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
              </svg>
            </button>
            <button
              className="action-icon-btn"
              onClick={() => onViewLogs(s.serverId)}
              title="View History Logs"
              style={{
                color: "var(--brand-blue)",
                borderColor: "rgba(59, 130, 246, 0.3)"
              }}
            >
              {/* Eye / Logs Icon */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>
            <button
              className="action-icon-btn"
              onClick={() => setDeleteDialogServer(s)}
              title="Delete Server Record"
              style={{
                color: "#f57c7c",
                borderColor: "rgba(245, 124, 124, 0.3)"
              }}
            >
              {/* Trash / Delete Icon */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          </div>
        );
      }
    }
  ], [isAnyUpdateRunning, onTriggerAction, onViewLogs, getActionButtonText, isUpdateRunning]);

  const handleRowPrepared = useCallback(applyRowStatusClass, []);

  const filteredServers = useMemo(() => servers.filter(s => {
    if (serverFilter === "all") return true;
    const sType = (s.serverType || "Live").toLowerCase();
    return sType === serverFilter;
  }), [servers, serverFilter]);

  const autoRefreshControl = useMemo(() => (
    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginRight: "6px" }}>
      <span style={{ fontSize: "var(--fs-xs)", color: "var(--text-muted)", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}>Auto Refresh:</span>
      <select
        className="toolbar-select"
        style={{
          minWidth: "90px",
          height: "28px",
          padding: "2px 6px",
          borderRadius: "var(--radius-sm)",
          fontSize: "var(--fs-xs)",
          fontWeight: "500",
          background: "var(--input-bg)",
          borderColor: "var(--input-border)",
          color: "var(--text-color)",
          cursor: "pointer",
          outline: "none"
        }}
        value={refreshRate}
        onChange={(e) => setRefreshRate(Number(e.target.value))}
      >
        <option value={60000}>1m</option>
        <option value={120000}>2m</option>
        <option value={300000}>5m</option>
        <option value={600000}>10m</option>
        <option value={900000}>15m</option>
        <option value={1800000}>30m</option>
        <option value={0}>Off</option>
      </select>
    </div>
  ), [refreshRate, setRefreshRate]);

  const updateAllControl = useMemo(() => (
    <button
      className="refresh-btn"
      onClick={onUpdateAll}
      style={{
        borderColor: "rgba(82, 196, 141, 0.4)",
        color: "#52c48d",
        background: "rgba(82, 196, 141, 0.05)"
      }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 4 23 10 17 10" />
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
      </svg>
      Update All
    </button>
  ), [onUpdateAll]);

  const refreshControl = useMemo(() => (
    <button
      className="refresh-btn"
      onClick={onRefresh}
      disabled={loading}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: loading ? "spin 1s linear infinite" : "none" }}>
        <polyline points="23 4 23 10 17 10" />
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
      </svg>
      {loading ? "Refreshing..." : "Refresh"}
    </button>
  ), [onRefresh, loading]);

  return (
    <section className="portal-panel">
      <div style={{ height: "auto", width: "100%" }}>
        <DataGridList
          data={filteredServers}
          keyExpr="serverId"
          columns={columns}
          height="auto"
          toolbarVisible={true}
          filterButtonVisible={true}
          gridTitle="Servers"
          showGridTitle={true}
          filterButtonText={`Filter: ${serverFilter.toUpperCase()}`}
          filterData={filterOptions}
          onFilterItemClick={(e) => setServerFilter(e.itemData.value)}
          autoRefreshControl={autoRefreshControl}
          updateAllControl={updateAllControl}
          refreshControl={refreshControl}
          showSearchPanel={true}
          showHeaderFilter={false}
          showPager={true}
          pageSize={10}
          focusedRowEnabled={false}
          selectionMode="none"
          onRowPrepared={handleRowPrepared}
          isLoading={loading}
        />
      </div>

      {editDialogServer && (
        <ServerEditDialog
          server={editDialogServer}
          serverTypes={serverTypes}
          onClose={() => setEditDialogServer(null)}
          onSave={(payload) => handleSaveServer(editDialogServer, payload)}
        />
      )}

      {deleteDialogServer && (
        <ConfirmDialog
          title="Delete server record"
          message={`Remove "${deleteDialogServer.displayName || deleteDialogServer.groupName || deleteDialogServer.serverId}" (${deleteDialogServer.serverId}) from the monitor? This deletes the portal record only - it does not uninstall anything on the server.`}
          confirmText="Delete"
          danger
          onConfirm={() => handleDeleteServer(deleteDialogServer)}
          onClose={() => setDeleteDialogServer(null)}
        />
      )}

      {updateDialogServer && (
        <ServerUpdateDialog
          server={updateDialogServer}
          onClose={() => setUpdateDialogServer(null)}
          onApply={(versions) => onTriggerAction(updateDialogServer.serverId, "Update", versions)}
        />
      )}
    </section>
  );
}
